/**
 * Item Card Configuration and Styling Presets
 * 
 * This file contains predefined configurations and styling options
 * for the ItemCard and ItemCardContainer components.
 */

// Default configuration for ItemCard
export const DEFAULT_CARD_CONFIG = {
  showPAR: true,
  showMOQ: true,
  showDelete: true,
  showPosition: true,
  enableDrag: true,
  dragType: 'item-card'
};

// Default configuration for ItemCardContainer
export const DEFAULT_CONTAINER_CONFIG = {
  groupByLocation: true,
  showLocationHeaders: true,
  sortBy: 'position', // 'position', 'name', 'location', 'vendor', 'par', 'moq'
  sortAscending: true
};

// Preset configurations for different use cases
export const PRESET_CONFIGS = {
  // Full-featured card for count sessions
  COUNT_SESSION: {
    card: {
      showPAR: true,
      showMOQ: true,
      showDelete: true,
      showPosition: true,
      enableDrag: true,
      dragType: 'count-item'
    },
    container: {
      groupByLocation: true,
      showLocationHeaders: true,
      sortBy: 'position',
      sortAscending: true
    }
  },

  // Read-only display card
  DISPLAY_ONLY: {
    card: {
      showPAR: false,
      showMOQ: false,
      showDelete: false,
      showPosition: true,
      enableDrag: false,
      dragType: 'item-card'
    },
    container: {
      groupByLocation: true,
      showLocationHeaders: true,
      sortBy: 'name',
      sortAscending: true
    }
  },

  // Inventory management card
  INVENTORY: {
    card: {
      showPAR: true,
      showMOQ: true,
      showDelete: true,
      showPosition: false,
      enableDrag: true,
      dragType: 'inventory-item'
    },
    container: {
      groupByLocation: true,
      showLocationHeaders: true,
      sortBy: 'name',
      sortAscending: true
    }
  },

  // Simple list without grouping
  SIMPLE_LIST: {
    card: {
      showPAR: false,
      showMOQ: false,
      showDelete: false,
      showPosition: false,
      enableDrag: false,
      dragType: 'item-card'
    },
    container: {
      groupByLocation: false,
      showLocationHeaders: false,
      sortBy: 'name',
      sortAscending: true
    }
  },

  // Template editor card
  TEMPLATE_EDITOR: {
    card: {
      showPAR: true,
      showMOQ: true,  // Enable MOQ for template editing
      showDelete: true,
      showPosition: true,
      enableDrag: true,
      dragType: 'template-item'
    },
    container: {
      groupByLocation: true,
      showLocationHeaders: true,
      sortBy: 'position',
      sortAscending: true
    }
  },

  // Group editor (count template UI): dense rows, PAR only, same drag type as legacy editor
  GROUP_EDITOR: {
    card: {
      showPAR: true,
      showMOQ: false,
      showDelete: true,
      showPosition: false,
      enableDrag: true,
      dragType: 'template-item',
      iconDelete: true,
      hideLocationVendorRows: true
    },
    container: {
      groupByLocation: true,
      showLocationHeaders: true,
      sortBy: 'position',
      sortAscending: true
    }
  }
};

// Default styling for ItemCard
export const DEFAULT_CARD_STYLING = {
  card: {
    minHeight: 64,
    cursor: 'move'
  },
  position: {
    fontSize: '1rem',
    fontWeight: 'bold',
    color: '#000000',
    opacity: '0.5',
    lineHeight: '1'
  },
  dragHandle: {
    color: 'rgb(36, 38, 41)',
    fontWeight: '100',
    fontSize: '1rem'
  },
  itemName: {
    fontSize: '0.75rem', /* Reduced from 0.8rem to match console changes */
    color: 'var(--bs-emphasis-color)',
    fontWeight: '600',
    lineHeight: '1.2'
  },
  location: {
    fontSize: '0.75rem',
    color: 'var(--bs-secondary-color)',
    marginBottom: '2px',
    textAlign: 'left'
  },
  vendor: {
    fontSize: '0.75rem',
    color: 'var(--bs-secondary-color)',
    textAlign: 'left'
  },
  inputPanel: {
    backgroundColor: 'rgba(248, 249, 250, 0.5)',
    borderLeft: '2px solid var(--bs-border-color)'
  },
  parInput: {
    backgroundColor: '#fff3cd',
    border: '1px solid #f0ad4e'
  },
  moqInput: {
    backgroundColor: '#d1ecf1',
    border: '1px solid #17a2b8'
  },
  deleteButton: {
    color: '#dc3545',
    fontSize: '16px',
    fontWeight: 'bold'
  }
};

// Default styling for ItemCardContainer
export const DEFAULT_CONTAINER_STYLING = {
  container: {},
  locationHeader: {
    color: '#5A4A42',
    fontSize: '0.9rem',
    fontWeight: '600',
    marginBottom: '0.5rem',
    paddingBottom: '0.25rem',
    borderBottom: '1px solid #dee2e6'
  },
  locationGroup: {
    marginBottom: '1.5rem'
  },
  emptyState: {
    color: '#6c757d',
    fontStyle: 'italic'
  }
};

// Preset styling themes
export const STYLING_THEMES = {
  // Default theme (matches current design)
  DEFAULT: {
    card: DEFAULT_CARD_STYLING,
    container: DEFAULT_CONTAINER_STYLING
  },

  // Compact theme for dense layouts
  COMPACT: {
    card: {
      ...DEFAULT_CARD_STYLING,
      card: {
        ...DEFAULT_CARD_STYLING.card,
        minHeight: 48
      },
      itemName: {
        ...DEFAULT_CARD_STYLING.itemName,
        fontSize: '0.75rem'
      },
      location: {
        ...DEFAULT_CARD_STYLING.location,
        fontSize: '0.7rem'
      },
      vendor: {
        ...DEFAULT_CARD_STYLING.vendor,
        fontSize: '0.7rem'
      }
    },
    container: {
      ...DEFAULT_CONTAINER_STYLING,
      locationGroup: {
        ...DEFAULT_CONTAINER_STYLING.locationGroup,
        marginBottom: '1rem'
      }
    }
  },

  // Large theme for accessibility
  LARGE: {
    card: {
      ...DEFAULT_CARD_STYLING,
      card: {
        ...DEFAULT_CARD_STYLING.card,
        minHeight: 80
      },
      itemName: {
        ...DEFAULT_CARD_STYLING.itemName,
        fontSize: '0.9rem'
      },
      location: {
        ...DEFAULT_CARD_STYLING.location,
        fontSize: '0.8rem'
      },
      vendor: {
        ...DEFAULT_CARD_STYLING.vendor,
        fontSize: '0.8rem'
      },
      position: {
        ...DEFAULT_CARD_STYLING.position,
        fontSize: '1.2rem'
      }
    },
    container: {
      ...DEFAULT_CONTAINER_STYLING,
      locationHeader: {
        ...DEFAULT_CONTAINER_STYLING.locationHeader,
        fontSize: '1rem'
      },
      locationGroup: {
        ...DEFAULT_CONTAINER_STYLING.locationGroup,
        marginBottom: '2rem'
      }
    }
  },

  // Minimal theme for clean layouts
  MINIMAL: {
    card: {
      ...DEFAULT_CARD_STYLING,
      inputPanel: {
        ...DEFAULT_CARD_STYLING.inputPanel,
        backgroundColor: 'transparent',
        borderLeft: '1px solid var(--bs-border-color)'
      }
    },
    container: {
      ...DEFAULT_CONTAINER_STYLING,
      locationHeader: {
        ...DEFAULT_CONTAINER_STYLING.locationHeader,
        borderBottom: 'none',
        fontWeight: '500'
      }
    }
  },

  GROUP_EDITOR: {
    card: {
      ...DEFAULT_CARD_STYLING,
      card: {
        ...DEFAULT_CARD_STYLING.card,
        minHeight: 44,
        padding: '0.25rem'
      },
      itemName: {
        ...DEFAULT_CARD_STYLING.itemName,
        fontSize: '0.8rem',
        WebkitLineClamp: '1'
      },
      inputPanel: {
        ...DEFAULT_CARD_STYLING.inputPanel,
        position: 'static',
        left: 'auto',
        right: 'auto',
        top: 'auto',
        bottom: 'auto',
        width: 'auto',
        flexDirection: 'row',
        alignItems: 'center',
        gap: '4px',
        borderLeft: 'none',
        backgroundColor: 'transparent',
        paddingLeft: '0.35rem',
        marginLeft: '0.35rem',
        borderLeftWidth: '1px',
        borderLeftStyle: 'solid',
        borderLeftColor: 'var(--bs-border-color)'
      }
    },
    container: {
      ...DEFAULT_CONTAINER_STYLING,
      locationHeader: {
        ...DEFAULT_CONTAINER_STYLING.locationHeader,
        fontSize: '0.8rem',
        marginBottom: '0.35rem',
        paddingBottom: '0.15rem'
      },
      locationGroup: {
        ...DEFAULT_CONTAINER_STYLING.locationGroup,
        marginBottom: '0.65rem'
      }
    }
  }
};

// Utility functions for configuration
export const mergeConfigs = (baseConfig, customConfig) => {
  return {
    ...baseConfig,
    ...customConfig
  };
};

export const mergeStyling = (baseStyling, customStyling) => {
  const merged = { ...baseStyling };
  
  Object.keys(customStyling).forEach(key => {
    if (typeof customStyling[key] === 'object' && !Array.isArray(customStyling[key])) {
      merged[key] = { ...merged[key], ...customStyling[key] };
    } else {
      merged[key] = customStyling[key];
    }
  });
  
  return merged;
};

// Get preset configuration
export const getPresetConfig = (presetName) => {
  return PRESET_CONFIGS[presetName] || PRESET_CONFIGS.COUNT_SESSION;
};

// Get styling theme
export const getStylingTheme = (themeName) => {
  return STYLING_THEMES[themeName] || STYLING_THEMES.DEFAULT;
};

// Database Browser Configuration Presets
export const DATABASE_BROWSER_PRESETS = {
  // Full-featured browser for count sessions
  COUNT_SESSION: {
    showSearch: true,
    showFilters: true,
    showAddButton: true,
    showItemCount: true,
    expandableItems: true,
    addButtonText: "Add"
  },

  // Simple browser without filters
  SIMPLE: {
    showSearch: true,
    showFilters: false,
    showAddButton: true,
    showItemCount: true,
    expandableItems: false,
    addButtonText: "Select"
  },

  // Read-only browser for display
  DISPLAY_ONLY: {
    showSearch: true,
    showFilters: true,
    showAddButton: false,
    showItemCount: true,
    expandableItems: true,
    addButtonText: ""
  },

  // Compact browser for small spaces
  COMPACT: {
    showSearch: true,
    showFilters: false,
    showAddButton: true,
    showItemCount: false,
    expandableItems: false,
    addButtonText: "+"
  }
};

// Database Column Configuration Presets
export const DATABASE_COLUMN_PRESETS = {
  // Standard database column
  STANDARD: {
    title: "Add Items from Database",
    subtitle: "Search and add items from your inventory database",
    icon: "database",
    showHeader: true
  },

  // Inventory selection column
  INVENTORY_SELECTION: {
    title: "Select Inventory Items",
    subtitle: "Choose items from your inventory",
    icon: "package",
    showHeader: true
  },

  // Template builder column
  TEMPLATE_BUILDER: {
    title: "Add items to group",
    subtitle: "Build your group by adding items",
    icon: "plus-circle",
    showHeader: true
  },

  // Simple picker without subtitle
  SIMPLE_PICKER: {
    title: "Choose Items",
    subtitle: "",
    icon: "list",
    showHeader: true
  },

  // No header version
  HEADERLESS: {
    title: "",
    subtitle: "",
    icon: "",
    showHeader: false
  }
};

// Get database browser preset
export const getDatabaseBrowserPreset = (presetName) => {
  return DATABASE_BROWSER_PRESETS[presetName] || DATABASE_BROWSER_PRESETS.COUNT_SESSION;
};

// Get database column preset
export const getDatabaseColumnPreset = (presetName) => {
  return DATABASE_COLUMN_PRESETS[presetName] || DATABASE_COLUMN_PRESETS.STANDARD;
};
